import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import * as apiActions from 'api-actions'
import { selectors } from '../map/search/reducer'
import { routerActions } from 'react-router-redux'
import { buildSearchQueryString, useCommunity, useCommunityText } from 'utils'
import { Content, Header, Footer, RenderedHtml } from 'components'
import {
  FindDataCardIcon,
  FindProgramsCardIcon,
  FindSchoolsCardIcon,
  JustExploreCardIcon,
  ImportContactsIcon,
  BigHandshakeIcon,
  BigProfessionalDevelopmentIcon,
} from 'components/icons'
import { get } from 'lodash'
import { selectors as globalSelectors } from 'global-reducer'
import { SearchForm } from '../map/search/forms'
import CommunityImage from '../../components/community-image'
import YoutubeVideoModal from './components/youtube-video-modal'
import GenericCard from './components/generic-card'

const propTypes = {
  children: PropTypes.node,
  fetchSearchOptions: PropTypes.func.isRequired,
  push: PropTypes.func.isRequired,
  searchOptions: PropTypes.object,
  mostRecentPublishedSchoolYear: Types.schoolYear.isRequired,
}

const defaultProps = {
  children: null,
  searchOptions: null,
}

function CommunityHome({
  children,
  push,
  fetchSearchOptions,
  searchOptions,
  mostRecentPublishedSchoolYear,
}) {
  const community = useCommunity()
  const videoImage = community.defaultBranding.videoImpage
  const isSwpa = community.subdomain === 'swpa'
  const hideSchoolSearchToggle = get(community, 'schoolDisabled', false)
  const t = useCommunityText()
  const homeHero = get(community, 'defaultBranding.mapHero.src')
  const homeHeroAttribution = get(
    community,
    'defaultBranding.mapHero.attribution'
  )

  const blockList = useMemo(() => {
    return get(community, 'search.options.blockList', [])
  }, [community])

  useEffect(() => {
    Promise.resolve(fetchSearchOptions({ blockList })).catch(() => {})
  }, [])

  const search = useCallback((params) => {
    return push({
      pathname: '/search',
      search: buildSearchQueryString(params),
    })
  })

  const tagline = t('map.tagline') || t('tagline')

  const [youtubeVideoModalTarget, setYoutubeVideoModalTarget] = useState('')
  const welcomeVideoMediaAltHtml = t('map.videoModal.mediaAlternativeHtml')
  const hasWelcomeVideoMediaAlt =
    typeof welcomeVideoMediaAltHtml === 'string' &&
    welcomeVideoMediaAltHtml.trim().length > 0

  function getExploreCardIcon() {
    if (isSwpa) {
      return (
        <div className="image-block">
          <BigHandshakeIcon />
        </div>
      )
    }
    return <JustExploreCardIcon />
  }

  function getFindDataCardIcon() {
    if (isSwpa) {
      return <BigProfessionalDevelopmentIcon />
    }

    return <FindDataCardIcon />
  }
  return (
    <React.Fragment>
      <div className="map">
        <Header showHomeLink={false} />
        <Content id="content">
          <header className="map-page-header">
            <div className="content-block-container">
              <div className="text-block">
                <RenderedHtml tag="h1">{tagline}</RenderedHtml>
              </div>
            </div>
            {homeHero && (
              <div className="map-hero-container">
                <div
                  className="image-block"
                  style={{ background: `url(${homeHero}) no-repeat center` }}
                  aria-hidden="true"
                />
                {homeHeroAttribution && (
                  <span className="image-attribution">
                    {homeHeroAttribution}
                  </span>
                )}
              </div>
            )}
          </header>
          <section className="search-block-widget">
            <div className="content-block-container card">
              <svg
                width="82px"
                height="82px"
                viewBox="0 0 82 82"
                version="1.1"
                className="location-image"
                aria-hidden="true"
              >
                <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                  <g id="Group-Copy" transform="translate(3.000000, 3.000000)">
                    <circle
                      stroke="#FFFFFF"
                      strokeWidth="6"
                      fill="#000"
                      cx="38"
                      cy="38"
                      r="38"
                    ></circle>
                    <path
                      d="M38,19 C45.1797017,19 51,25.1670054 51,32.7743921 C51,40.3817788 39.3,57.5682978 38,57.5682978 C36.7,57.5682978 25,40.3817788 25,32.7743921 C25,25.1670054 30.8202983,19 38,19 Z M38.2,27 C35.3281193,27 33,29.4668022 33,32.5097568 C33,35.5527115 35.3281193,38.0195137 38.2,38.0195137 C41.0718807,38.0195137 43.4,35.5527115 43.4,32.5097568 C43.4,29.4668022 41.0718807,27 38.2,27 Z"
                      fill="#FFFFFF"
                    ></path>
                  </g>
                </g>
              </svg>{' '}
              <h2>{t('map.welcomeSearchHeader')}</h2>
              <div className="search-form">
                <SearchForm
                  name="index-search-formm"
                  searchOptions={searchOptions}
                  initialValues={{
                    type: hideSchoolSearchToggle
                      ? Types.PARTNER_SEARCH_TYPE
                      : Types.SCHOOL_SEARCH_TYPE,
                    schoolYear: get(mostRecentPublishedSchoolYear, 'id'),
                  }}
                  hideTypeToggle={hideSchoolSearchToggle}
                  onSubmit={search}
                />
              </div>
            </div>
          </section>
          <section className="video-and-welcome-block-container">
            <div className="card">
              <h1 className="welcome-header">
                <RenderedHtml>{t('map.videoWelcomeTitle')}</RenderedHtml>
              </h1>
              <RenderedHtml>{t('map.videoWelcomeMessage')}</RenderedHtml>
            </div>
            <div className="video-card">
              <button
                type="button"
                className="video-button"
                onClick={() => setYoutubeVideoModalTarget('open')}
                aria-label={
                  typeof videoImage.alt === 'string' && videoImage.alt.trim()
                    ? videoImage.alt.trim()
                    : 'Play introductory video'
                }
              >
                <CommunityImage
                  image={{ ...videoImage, alt: '' }}
                  className="video-button-image"
                />
              </button>
              <YoutubeVideoModal
                isOpen={youtubeVideoModalTarget !== ''}
                onClose={() => {
                  setYoutubeVideoModalTarget('')
                }}
                width={t('map.videoModal.width')}
                height={t('map.videoModal.height')}
                srcUrl={t('map.videoModal.srcUrl')}
                iframeTitle={
                  t('map.videoModal.iframeTitle') ||
                  `${
                    community.name || community.subdomain
                  } artlook map welcome video`
                }
                className="video-modal"
                mediaAlternative={
                  hasWelcomeVideoMediaAlt ? (
                    <RenderedHtml className="youtube-video-modal-media-alt-body">
                      {welcomeVideoMediaAltHtml}
                    </RenderedHtml>
                  ) : null
                }
              />
            </div>
          </section>
          <section className="cards-block-container flex-vertical">
            <div className="cards-block-container-title ">
              <RenderedHtml>{t('map.explorationHeader')}</RenderedHtml>
            </div>
            <div className="cards-block-container-sub-title">
              <h2>{t('map.explorationSubTitle')}</h2>
            </div>
          </section>
          <section className="cards-block-container top-row flex-vertical">
            <div className="row">
              <GenericCard
                className="card col-6"
                icon={<FindSchoolsCardIcon />}
                title={t('map.findSchoolsCard.title')}
                description={
                  <RenderedHtml>{t('map.findSchoolsCard.text')}</RenderedHtml>
                }
              />
              <GenericCard
                className="card col-6"
                icon={<FindProgramsCardIcon />}
                title={t('map.findProgramsCard.title')}
                description={
                  <RenderedHtml>{t('map.findProgramsCard.text')}</RenderedHtml>
                }
              />
            </div>
            <div className="row">
              <GenericCard
                className="card col-6"
                icon={getFindDataCardIcon()}
                title={t('map.findDataCard.title')}
                description={
                  <RenderedHtml>{t('map.findDataCard.text')}</RenderedHtml>
                }
              />
              <GenericCard
                className="card col-6"
                icon={getExploreCardIcon()}
                title={t('map.justExploreCard.title')}
                description={
                  <RenderedHtml>{t('map.justExploreCard.text')}</RenderedHtml>
                }
              />
            </div>
          </section>
          <section className="landing-content-block-container">
            <div className="colored-block grey-block">
              <div className="inner">
                <h2>
                  <ImportContactsIcon />{' '}
                  <RenderedHtml>{t('map.welcomeHeader')}</RenderedHtml>
                </h2>
                <RenderedHtml>{t('map.welcomeMessage')}</RenderedHtml>
              </div>
            </div>
          </section>

          {/* Community Specific Content */}
          {children}
        </Content>
        <Footer hideLogins />
      </div>
    </React.Fragment>
  )
}

CommunityHome.propTypes = propTypes
CommunityHome.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    searchOptions: selectors.searchOptions(state),
    mostRecentPublishedSchoolYear:
      globalSelectors.mostRecentPublishedSchoolYear(state),
  }
}

const mapDispatchToProps = {
  push: routerActions.push,
  fetchSearchOptions: apiActions.fetchSearchOptions,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  CommunityHome
)
